import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Table,
  Text,
} from "@chakra-ui/react";
import { Hint } from "@/ui/Hint";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { LuFileText, LuSearch, LuX } from "react-icons/lu";
import { ModalSearchProduct } from "@/core/products/components/ModalSearchProduct";
import { useCreateReposition } from "../useCases/useCreateReposition";
import type { CreateReposition, Reposition } from "../types/Replenishment";
import { useAuth } from "@/shared/session";
import { useGetStorage } from "@/core/storages/useCases/useGetStorage";
import type { ProductState } from "@/core/products/components/ProductsTable";
import DialogInvoiceScan from "./DialogInvoiceScan";
import { ReplenishmentScanResponse } from "@/shared/core-replenishments";
import { prettifyRut } from "@/core/common/utils/rutUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ReplenishmentFormInput,
  replenishmentSchema,
} from "../schemas/replenishmentSchema";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";
import { NativeSelectField, NativeSelectRoot } from "@/ui/chakra/native-select";
import { NumberInputField, NumberInputRoot } from "@/ui/chakra/number-input";

export interface ModalReplenishmentProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Reposition;
  onSubmit: (data: CreateReposition, invoiceFile?: File | null) => void;
}

const FORM_ID = "replenishment-form";

const headerProps = {
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "wider",
  textTransform: "uppercase",
  color: "fg.muted",
  whiteSpace: "nowrap",
} as const;

export const ModalReplenishment = ({
  isOpen,
  onClose,
  initialData,
  onSubmit,
}: ModalReplenishmentProps) => {
  const [searchProductModal, setSearchProductModal] = useState(false);

  const [invoiceFile, setInvoiceFile] = useState<File | null | undefined>(null);

  const [customError, setCustomError] = useState<string | null>(null);
  const { user, session } = useAuth();

  const { data: storages } = useGetStorage(session?.zone || "");
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    setValue,
    watch,
    reset,
  } = useForm<ReplenishmentFormInput>({
    resolver: zodResolver(replenishmentSchema),
    defaultValues: {
      supplierRut: initialData?.supplierRut ?? "",
      invoiceNumber: initialData?.invoiceNumber ?? "",
      storageId: initialData?.storageId ?? "",
      zoneId: initialData?.zoneId ?? (session?.zone || ""),
      products: initialData?.products
        ? initialData.products.map((product) => ({
            data: {
              id: product.id,
              stock: product.stock,
              description: product.description,
              unit: product.unit ?? "",
              location: product.location ?? "",
              minimumStock: product.minimumStock ?? 0,
              storageId: product.storageId ?? "",
              storage: product.storage ?? { id: "", name: "" },
              converter: product.converter ?? 0,
              converterTypeId: product.converterTypeId ?? "",
              converterType: product.converterType ?? {
                id: "",
                name: "",
              },
              enabled: product.enabled ?? true,
            },
            amount: product.repositionProduct?.stockIncoment || 0,
            price: product.repositionProduct?.price || 0,
          }))
        : [],
    },
  });

  const { products, storageId } = watch();
  const { isPending, isError, error } = useCreateReposition();

  const handleAddProduct = (product: ProductState) => {
    if (products.some((p) => p.data.id === product.data.id)) return;
    setCustomError(null);
    setValue("products", [...products, product]);
  };

  const handleUpdateStockIncoment = (
    productId: string,
    newStockIncoment: number,
  ) => {
    setValue(
      "products",
      products.map((product) =>
        product.data.id === productId
          ? { ...product, amount: newStockIncoment }
          : product,
      ),
    );
  };

  const handleUpdatePrice = (productId: string, newPrice: number) => {
    setValue(
      "products",
      products.map((product) =>
        product.data.id === productId
          ? { ...product, price: newPrice }
          : product,
      ),
    );
  };

  const handleRemoveProduct = (productId: string) => {
    setValue(
      "products",
      products.filter((product) => product.data.id !== productId),
    );
  };

  const onSubmitForm = (data: ReplenishmentFormInput) => {
    if (products.length === 0) {
      setCustomError("Debe agregar al menos un producto.");
      return;
    }
    if (products.some((product) => !(Number(product.amount) > 0))) {
      setCustomError(
        "Todos los productos deben tener una cantidad a reponer mayor a 0.",
      );
      return;
    }

    const formattedProducts = products.map((product) => ({
      id: product.data.id,
      stock: product.data.stock,
      stockIncoment: product.amount ?? 0,
      price: Number(product.price) || 0,
    }));

    const repositionData: CreateReposition = {
      ...data,
      creatorId: user?.id ?? "defaultCreatorId",
      products: formattedProducts,
      supplierRut: String(data.supplierRut),
      invoiceNumber: String(data.invoiceNumber),
    };

    setCustomError(null);
    onSubmit(repositionData, invoiceFile);
    reset();
  };

  const handleOnSubmitInvoice = (
    res: ReplenishmentScanResponse,
    invoiceFile: File | null,
  ) => {
    setInvoiceFile(invoiceFile);

    if (!res) return;
    setValue("supplierRut", res.invoiceRut);
    setValue("invoiceNumber", res.invoiceNumber);
    setValue(
      "products",
      res.products?.map((item) => ({
        data: {
          id: item.product.id,
          stock: item.product.stock,
          description: item.product.description,
          unit: item.product.unit ?? "",
          location: item.product.location ?? "",
          minimumStock: item.product.minimumStock ?? 0,
          storageId: item.product.storageId ?? "",
          storage: item.product.storage ?? {
            id: "",
            name: "",
          },
          converter: item.product.converter ?? 0,
          converterTypeId: item.product.converterTypeId ?? "",
          converterType: item.product.converterType ?? {
            id: "",
            name: "",
          },
          enabled: item.product.enabled ?? true,
        },
        amount: item.quantity ?? 0,
        price: item.price ?? 0,
      })),
    );
  };

  const rutField = register("supplierRut");

  return (
    <>
      <DialogRoot isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <DialogContent maxW="960px">
          <DialogHeader
            flexDirection="column"
            alignItems="flex-start"
            gap={1}
            borderBottomWidth="1px"
            borderColor="border"
          >
            <DialogTitle>
              {initialData ? "Editar reposición" : "Crear reposición"}
            </DialogTitle>
            <DialogDescription>
              Ingresa la factura del proveedor y los productos que entran a
              bodega.
            </DialogDescription>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody display="flex" flexDirection="column" gap={6} pt={5}>
            <form
              id={FORM_ID}
              onSubmit={handleSubmit(onSubmitForm)}
              style={{ display: "contents" }}
            >
              <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                <Field
                  label="RUT del proveedor"
                  required
                  invalid={!!errors.supplierRut}
                  errorText={errors.supplierRut?.message?.toString()}
                >
                  <Input
                    {...rutField}
                    onChange={(e) => {
                      e.target.value = prettifyRut(e.target.value);
                      rutField.onChange(e);
                    }}
                    placeholder="76.123.456-7"
                  />
                </Field>
                <Field
                  label="Número de factura"
                  required
                  invalid={!!errors.invoiceNumber}
                  errorText={errors.invoiceNumber?.message?.toString()}
                >
                  <Input
                    {...register("invoiceNumber", {
                      required: "El número de factura es obligatorio",
                    })}
                    placeholder="000123"
                  />
                </Field>
                <Field
                  label="Bodega"
                  required
                  invalid={!!errors.storageId}
                  errorText={errors.storageId?.message?.toString()}
                >
                  <NativeSelectRoot>
                    <NativeSelectField
                      placeholder="Selecciona..."
                      {...register("storageId")}
                    >
                      {storages?.map((storage) => (
                        <option key={storage.id} value={storage.id}>
                          {storage.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </SimpleGrid>

              <Flex
                align="center"
                justify="space-between"
                gap={4}
                wrap="wrap"
                p={4}
                borderWidth="1px"
                borderColor="border"
                rounded="lg"
                bg="bg.subtle"
              >
                <HStack gap={3} minW={0}>
                  <Box color="fg.muted" display="inline-flex">
                    <LuFileText size={20} />
                  </Box>
                  <Box minW={0}>
                    <Text fontSize="sm">
                      <Text as="span" fontWeight="semibold">
                        Factura
                      </Text>{" "}
                      <Text as="span" color="fg.muted">
                        · PDF, hasta 10 MB
                      </Text>
                    </Text>
                    <Text fontSize="xs" color="fg.muted" truncate>
                      {invoiceFile
                        ? invoiceFile.name
                        : initialData?.invoiceUrl
                          ? "Factura adjunta"
                          : "Aún no has subido la factura."}
                    </Text>
                  </Box>
                </HStack>
                <DialogInvoiceScan
                  onSucess={handleOnSubmitInvoice}
                  buttonLabel={
                    invoiceFile ? "Cambiar archivo" : "Subir archivo"
                  }
                />
              </Flex>

              {!invoiceFile && !initialData?.invoiceUrl && (
                <Hint>
                  La factura se escanea sola: el RUT del proveedor, el número y
                  los productos se completan automáticamente. Tarda un par de
                  minutos; puedes seguir llenando el resto mientras tanto y
                  revisar lo leído antes de guardar.
                </Hint>
              )}

              <Box>
                <Flex
                  align="center"
                  justify="space-between"
                  gap={4}
                  wrap="wrap"
                  mb={3}
                >
                  <Text fontWeight="semibold">
                    Productos{" "}
                    <Text as="span" color="fg.muted" fontWeight="normal">
                      · {products.length}
                    </Text>
                  </Text>
                  <Button
                    variant="outline"
                    fontWeight="normal"
                    color="fg.muted"
                    justifyContent="flex-start"
                    w={{ base: "full", sm: "360px" }}
                    onClick={() => setSearchProductModal(true)}
                    type="button"
                  >
                    <LuSearch />
                    Buscar producto por código o nombre...
                  </Button>
                </Flex>

                <Box
                  borderWidth="1px"
                  borderColor="border"
                  rounded="lg"
                  overflow="hidden"
                >
                  <Table.ScrollArea maxH="320px">
                    <Table.Root size="sm" variant="line" stickyHeader>
                      <Table.Header bg="bg.subtle">
                        <Table.Row>
                          <Table.ColumnHeader {...headerProps}>
                            Código
                          </Table.ColumnHeader>
                          <Table.ColumnHeader {...headerProps}>
                            Descripción
                          </Table.ColumnHeader>
                          <Table.ColumnHeader {...headerProps} textAlign="end">
                            Stock
                          </Table.ColumnHeader>
                          <Table.ColumnHeader {...headerProps} textAlign="end">
                            Cantidad
                          </Table.ColumnHeader>
                          <Table.ColumnHeader {...headerProps} textAlign="end">
                            Precio
                          </Table.ColumnHeader>
                          <Table.ColumnHeader width="1%" />
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {products.map((product) => (
                          <Table.Row key={product.data.id}>
                            <Table.Cell
                              fontWeight="semibold"
                              whiteSpace="nowrap"
                              maxW="140px"
                              truncate
                              title={product.data.id}
                            >
                              {product.data.externalProduct?.code ||
                                product.data.erpProduct?.erpCode ||
                                product.data.id}
                            </Table.Cell>
                            <Table.Cell minW="200px">
                              {product.data.description}
                            </Table.Cell>
                            <Table.Cell textAlign="end">
                              {product.data.stock}
                            </Table.Cell>
                            <Table.Cell textAlign="end">
                              <NumberInputRoot
                                size="sm"
                                w="110px"
                                ml="auto"
                                min={0}
                                invalid={
                                  !!customError && !(Number(product.amount) > 0)
                                }
                                value={String(product.amount ?? 0)}
                                onValueChange={(e) =>
                                  handleUpdateStockIncoment(
                                    product.data.id,
                                    Number(e.valueAsNumber) || 0,
                                  )
                                }
                              >
                                <NumberInputField aria-label="Cantidad a reponer" />
                              </NumberInputRoot>
                            </Table.Cell>
                            <Table.Cell textAlign="end">
                              <NumberInputRoot
                                size="sm"
                                w="130px"
                                ml="auto"
                                min={0}
                                value={String(product.price ?? 0)}
                                onValueChange={(e) =>
                                  handleUpdatePrice(
                                    product.data.id,
                                    Number(e.valueAsNumber) || 0,
                                  )
                                }
                              >
                                <NumberInputField aria-label="Precio" />
                              </NumberInputRoot>
                            </Table.Cell>
                            <Table.Cell textAlign="end">
                              <IconButton
                                aria-label="Quitar producto"
                                size="sm"
                                variant="ghost"
                                color="fg.muted"
                                onClick={() =>
                                  handleRemoveProduct(product.data.id)
                                }
                              >
                                <LuX />
                              </IconButton>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                        {products.length === 0 && (
                          <Table.Row>
                            <Table.Cell
                              colSpan={6}
                              textAlign="center"
                              py={8}
                              color="fg.muted"
                            >
                              Aún no hay productos. Busca uno arriba para
                              agregarlo.
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                </Box>

                {customError && (
                  <Text color="fg.error" fontSize="sm" mt={2}>
                    {customError}
                  </Text>
                )}

                {isError && (
                  <Box color="fg.error" fontSize="sm" mt={2}>
                    Ocurrió un error al procesar la reposición. Inténtalo de
                    nuevo.
                    <Text as="pre" fontSize="xs" whiteSpace="pre-wrap">
                      {JSON.stringify(error?.message, null, 2)}
                    </Text>
                  </Box>
                )}
              </Box>
            </form>
          </DialogBody>
          <DialogFooter borderTopWidth="1px" borderColor="border">
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button
              colorPalette="brand"
              form={FORM_ID}
              type="submit"
              loading={isPending || isSubmitting}
              minW="160px"
            >
              {initialData ? "Guardar cambios" : "Crear reposición"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      <ModalSearchProduct
        isOpen={searchProductModal}
        onClose={() => setSearchProductModal(false)}
        selected={products}
        onChange={handleAddProduct}
        productType="local"
        storageId={storageId}
      />
    </>
  );
};
